import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, setDoc, deleteDoc, Timestamp, writeBatch } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import useAdminAuth from '../../../hooks/useAdminAuth';
import { friendlyDate, friendlyTimestamp, toLocalDateStr } from '../../../utils/dateFormat';
import { HiOutlineTrash, HiPlus } from 'react-icons/hi2';
import { HiOutlineCog6Tooth } from 'react-icons/hi2';
const CLASS_COLORS = ['#4A7CFE', '#22C55E', '#F59E0B', '#EF4444', '#A855F7', '#06B6D4', '#F97316', '#EC4899'];

const AdminClassesPage = () => {
  const { isSuperAdmin, isTeacher, canCreate, canEdit, canDelete, isReadOnly, admin, filterByOwner } = useAdminAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '', subject: '', teacherId: '', room: '', color: '#4A7CFE', openDate: toLocalDateStr(new Date()),
  });

  const load = async () => {
    const [classSnap, userSnap] = await Promise.all([
      getDocs(collection(db, 'classes')),
      getDocs(collection(db, 'users')),
    ]);
    const allClasses = classSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const allUsers = userSnap.docs.map((d) => ({ uid: d.id, ...d.data() }));

    setClasses(filterByOwner(allClasses));
    setTeachers(allUsers.filter((u) => u.role === 'teacher'));
    setStudents(allUsers.filter((u) => u.role === 'student'));
    setLoaded(true);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({
      name: '', subject: '',
      teacherId: isTeacher ? admin.uid : '',
      room: '', color: '#4A7CFE',
      openDate: toLocalDateStr(new Date()),
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name) return;
    const teacher = teachers.find((t) => t.uid === form.teacherId);
    const data = {
      name: form.name,
      subject: form.subject,
      teacherId: form.teacherId,
      teacherName: teacher?.name || '',
      room: form.room,
      color: form.color,
      studentIds: [],
      students: 0,
      createdAt: Timestamp.fromDate(new Date(form.openDate + 'T00:00:00')),
    };

    const id = `class-${Date.now()}`;
    await setDoc(doc(db, 'classes', id), data);

    setShowModal(false);
    load();
  };

  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState('');

  const handleDelete = async (cls) => {
    const studentCount = cls.students || cls.studentIds?.length || 0;
    const msg = studentCount > 0
      ? `'${cls.name}' 클래스를 삭제하시겠습니까?\n\n수강 학생 ${studentCount}명의 클래스 정보도 함께 삭제됩니다.`
      : `'${cls.name}' 클래스를 삭제하시겠습니까?`;
    if (!window.confirm(msg)) return;
    if (!window.confirm('정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;

    setDeleting(true);
    const classRef = doc(db, 'classes', cls.id);
    const subCollections = ['attendance', 'assignments', 'grades', 'notices', 'materials', 'videos', 'logs', 'memos'];
    const subLabels = { attendance: '출석 기록', assignments: '과제', grades: '성적', notices: '공지사항', materials: '수업 자료', videos: '수업 동영상', logs: '수업 일지', memos: '메모' };

    try {
      for (const sub of subCollections) {
        setDeleteProgress(`${subLabels[sub]} 삭제 중...`);
        const snap = await getDocs(collection(db, 'classes', cls.id, sub));
        if (snap.docs.length > 0) {
          const batch = writeBatch(db);
          snap.docs.forEach(d => batch.delete(d.ref));
          await batch.commit();
        }
      }
      setDeleteProgress('클래스 삭제 중...');
      await deleteDoc(classRef);
      setDeleteProgress('완료!');
      await new Promise(r => setTimeout(r, 500));
    } catch (err) {
      console.error('클래스 삭제 실패:', err);
      alert('삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleting(false);
      setDeleteProgress('');
      load();
    }
  };

  return (
    <Container>
      {deleting && (
        <DeleteOverlay>
          <DeleteSpinner />
          <DeleteText>{deleteProgress}</DeleteText>
        </DeleteOverlay>
      )}
      <PageHeader>
        <div>
          <PageTitle>클래스 관리</PageTitle>
          <PageDesc>
            {isReadOnly && '(조회 전용) '}
            전체 {classes.length}개 클래스
          </PageDesc>
        </div>
        {canCreate && (
          <AddButton onClick={openCreate}>
            <HiPlus size={16} /> 클래스 추가
          </AddButton>
        )}
      </PageHeader>

      {!loaded ? null : classes.length === 0 ? (
        <EmptyState>
          <EmptyText>등록된 클래스가 없습니다</EmptyText>
          {canCreate && <EmptySub>클래스 추가 버튼을 눌러 시작하세요</EmptySub>}
        </EmptyState>
      ) : (
        <Grid>
          {classes.map((cls) => (
            <ClassCard key={cls.id}>
              <CardTop>
                <CardTopLeft>
                  <ColorDot $color={cls.color} />
                  {cls.createdAt && <CreatedDate>{friendlyTimestamp(cls.createdAt, { showDay: false })} 개설</CreatedDate>}
                </CardTopLeft>
                <CardActions>
                  {canEdit && (
                    <ManageBtn onClick={() => navigate(`/admin/classes/${cls.id}`)}>
                      <HiOutlineCog6Tooth size={14} /> 관리
                    </ManageBtn>
                  )}
                  {canDelete && (
                    <ActionBtn $danger onClick={() => handleDelete(cls)}>
                      <HiOutlineTrash size={16} />
                    </ActionBtn>
                  )}
                </CardActions>
              </CardTop>
              <ClassNameRow>
                <ClassName>{cls.name}</ClassName>
                {cls.subject && <ClassSubject>{cls.subject}</ClassSubject>}
              </ClassNameRow>
              <Divider />
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>담당 강사</InfoLabel>
                  <InfoValue>{cls.teacherName || '-'}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>학생 수</InfoLabel>
                  <InfoValue>{cls.students || 0}명</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>강의실</InfoLabel>
                  <InfoValue>{cls.room || '-'}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>출석 모드</InfoLabel>
                  <InfoValue>{cls.attendanceMode === 'self' ? '위치 기반 (학생)' : '자동 (강사 체크)'}</InfoValue>
                </InfoItem>
              </InfoGrid>
              <ScheduleArea>
                <InfoLabel>수업 시간</InfoLabel>
                <ScheduleRow>
                  {cls.schedule?.length > 0 ? cls.schedule.map((s, i) => (
                    <ScheduleChip key={i}>{s.day} {s.time}</ScheduleChip>
                  )) : <span style={{fontSize:13,color:'#9CA3AF'}}>미등록</span>}
                </ScheduleRow>
              </ScheduleArea>
              {(cls.supplements || []).filter(s => s.date >= toLocalDateStr(new Date())).length > 0 && (
                <ScheduleArea>
                  <InfoLabel>보강 수업</InfoLabel>
                  <ScheduleRow>
                    {cls.supplements.filter(s => s.date >= toLocalDateStr(new Date())).map((s, i) => (
                      <SupChip key={i}>{friendlyDate(s.date)} {s.time}{s.endTime ? `-${s.endTime}` : ''}</SupChip>
                    ))}
                  </ScheduleRow>
                </ScheduleArea>
              )}

            </ClassCard>
          ))}
        </Grid>
      )}

      {showModal && (
        <Overlay onClick={() => setShowModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>클래스 추가</ModalTitle>

            <Label>클래스명 *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder=""
            />

            <Label>과목</Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder=""
            />

            <Label>담당 강사 *</Label>
            {isTeacher ? (
              <FixedValue>{admin.name} (본인)</FixedValue>
            ) : (
              <Select
                value={form.teacherId}
                onChange={(e) => setForm({ ...form, teacherId: e.target.value })}
              >
                <option value="">강사 선택</option>
                {teachers.map((t) => (
                  <option key={t.uid} value={t.uid}>{t.name}</option>
                ))}
              </Select>
            )}

            <Label>개설일 *</Label>
            <Input
              type="date"
              value={form.openDate}
              onChange={(e) => setForm({ ...form, openDate: e.target.value })}
            />

            <Label>강의실</Label>
            <Input
              value={form.room}
              onChange={(e) => setForm({ ...form, room: e.target.value })}
              placeholder=""
            />

            <Label>색상</Label>
            <ColorGrid>
              {CLASS_COLORS.map((c) => (
                <ColorBtn
                  key={c}
                  type="button"
                  $color={c}
                  $active={form.color === c}
                  onClick={() => setForm({ ...form, color: c })}
                />
              ))}
            </ColorGrid>

            <ModalActions>
              <CancelBtn onClick={() => setShowModal(false)}>취소</CancelBtn>
              <SaveBtn onClick={handleSave} disabled={!form.name || (!form.teacherId && !isTeacher)}>
                추가
              </SaveBtn>
            </ModalActions>
          </Modal>
        </Overlay>
      )}
    </Container>
  );
};

export default AdminClassesPage;

const Container = styled.div`position:relative;`;

const DeleteOverlay = styled.div`
  position:fixed;inset:0;z-index:9999;
  background:rgba(0,0,0,0.7);
  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:20px;
`;
const DeleteSpinner = styled.div`
  width:48px;height:48px;border:4px solid rgba(255,255,255,0.3);border-top-color:white;border-radius:50%;
  animation:dspin 0.8s linear infinite;
  @keyframes dspin{to{transform:rotate(360deg);}}
`;
const DeleteText = styled.p`font-size:16px;font-weight:600;color:white;`;

const PageHeader = styled.div`display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;`;
const PageTitle = styled.h1`font-size:22px;font-weight:700;color:#111827;`;
const PageDesc = styled.p`font-size:13px;color:#9CA3AF;margin-top:2px;`;
const AddButton = styled.button`display:flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;background:#4A7CFE;color:white;font-size:13px;font-weight:600;&:hover{background:#3B6DE8;}`;

const EmptyState = styled.div`text-align:center;padding:60px 20px;background:white;border-radius:12px;border:1px solid #EAEDF3;`;
const EmptyText = styled.p`font-size:16px;font-weight:600;color:#6B7280;`;
const EmptySub = styled.p`font-size:13px;color:#9CA3AF;margin-top:4px;`;

const Grid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:16px;`;
const ClassCard = styled.div`background:white;border-radius:12px;border:1px solid #EAEDF3;padding:20px;`;
const CardTop = styled.div`display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;`;
const CardTopLeft = styled.div`display:flex;align-items:center;gap:8px;`;
const ColorDot = styled.div`width:10px;height:10px;border-radius:50%;background:${({ $color }) => $color};`;
const CreatedDate = styled.span`font-size:12px;color:#9CA3AF;font-weight:500;`;
const CardActions = styled.div`display:flex;gap:2px;`;
const ActionBtn = styled.button`padding:4px;border-radius:4px;color:${({ $danger }) => $danger ? '#EF4444' : '#9CA3AF'};&:hover{background:${({ $danger }) => $danger ? '#FEF2F2' : '#F3F4F6'};}`;
const ManageBtn = styled.button`display:flex;align-items:center;gap:4px;padding:4px 10px;border-radius:6px;font-size:12px;font-weight:600;color:#374151;background:#F3F4F6;border:1px solid #E5E7EB;&:hover{background:#E5E7EB;}`;
const ClassNameRow = styled.div`display:flex;align-items:baseline;gap:8px;`;
const ClassName = styled.h3`font-size:22px;font-weight:700;color:#111827;`;
const ClassSubject = styled.span`font-size:13px;color:#9CA3AF;`;
const Divider = styled.div`height:1px;background:#F3F4F6;margin:14px 0;`;
const InfoGrid = styled.div`display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;`;
const InfoItem = styled.div``;
const ScheduleArea = styled.div`margin-top:12px;`;
const ScheduleRow = styled.div`display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin-top:4px;`;
const ScheduleChip = styled.span`padding:5px 6px;background:#4A7CFE;color:white;font-size:11px;font-weight:700;border-radius:6px;text-align:center;white-space:nowrap;`;
const SupChip = styled.span`padding:5px 6px;background:#EF4444;color:white;font-size:11px;font-weight:700;border-radius:6px;text-align:center;white-space:nowrap;`;
const InfoLabel = styled.p`font-size:11px;color:#9CA3AF;margin-bottom:2px;`;
const InfoValue = styled.p`font-size:13px;font-weight:600;color:#374151;`;

const StudentChips = styled.div`display:flex;flex-wrap:wrap;gap:6px;margin-top:6px;`;
const StudentChip = styled.span`padding:3px 10px;border-radius:12px;font-size:12px;font-weight:500;background:#EEF2FF;color:#4A7CFE;`;

// Modal
const Overlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;z-index:50;`;
const Modal = styled.div`background:white;border-radius:16px;padding:24px;width:520px;max-width:90vw;max-height:90vh;overflow-y:auto;`;
const ModalTitle = styled.h2`font-size:18px;font-weight:700;color:#1E3A5F;margin-bottom:16px;`;
const Label = styled.label`display:block;font-size:13px;font-weight:600;color:#374151;margin-bottom:6px;margin-top:14px;`;
const Input = styled.input`width:100%;padding:10px 12px;border:1px solid #E5E7EB;border-radius:8px;font-size:14px;color:#111827;&:focus{border-color:#4A7CFE;outline:none;}`;
const Select = styled.select`width:100%;padding:10px 12px;border:1px solid #E5E7EB;border-radius:8px;font-size:14px;color:#111827;`;
const FixedValue = styled.div`padding:10px 12px;border:1px solid #E5E7EB;border-radius:8px;font-size:14px;color:#6B7280;background:#F9FAFB;`;

const ColorGrid = styled.div`display:flex;gap:8px;`;
const ColorBtn = styled.button`
  width:28px;height:28px;border-radius:50%;background:${({ $color }) => $color};
  border:3px solid ${({ $active, $color }) => $active ? $color : 'transparent'};
  outline:${({ $active }) => $active ? '2px solid #E5E7EB' : 'none'};
  outline-offset:1px;
`;

const ModalActions = styled.div`display:flex;gap:8px;justify-content:flex-end;margin-top:20px;`;
const CancelBtn = styled.button`padding:10px 20px;border-radius:8px;border:1px solid #E5E7EB;font-size:14px;color:#374151;&:hover{background:#F9FAFB;}`;
const SaveBtn = styled.button`padding:10px 20px;border-radius:8px;background:#4A7CFE;color:white;font-size:14px;font-weight:600;&:hover{background:#3B6DE8;}&:disabled{opacity:0.5;}`;
